import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DetailPageLayout from "../../../components/detail/DetailPageLayout";
import { instanceToken } from "../../../utils/constant";
import { dateInfoItems } from "../../../utils/detailUtils";
import {
  deleteDocument,
  downloadDocument,
  fetchDocument,
  formatLanguageLabel,
  isValidScope,
  openPdfBlob,
  SCOPES,
} from "./policyDocumentUtils";

export default function DetailPolicyDocument({ homeAlert }) {
  const navigate = useNavigate();
  const { scope, id } = useParams();
  const [doc, setDoc] = useState(null);
  const validScope = isValidScope(scope);

  useEffect(() => {
    if (!validScope) return;
    const load = async () => {
      try {
        const data = await fetchDocument(scope, instanceToken.token, id);
        setDoc(data);
      } catch (error) {
        console.error(error);
        homeAlert("Failed to load policy document.", true);
      }
    };
    load();
  }, [scope, id, validScope, homeAlert]);

  const handleRemove = async () => {
    try {
      await deleteDocument(scope, instanceToken.token, id);
      homeAlert("Policy document has been removed.", false);
      navigate(`/policy-documents?scope=${scope}`);
    } catch (error) {
      console.error(error);
      homeAlert("Error on server!", true);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await downloadDocument(scope, instanceToken.token, id);
      openPdfBlob(response.data, doc?.file_name || "policy-document.pdf");
    } catch (error) {
      console.error(error);
      homeAlert("Failed to download PDF.", true);
    }
  };

  if (!validScope) {
    return null;
  }

  const scopeTitle = scope === SCOPES.general ? "General" : "Customer";

  const infoItems = doc
    ? [
        { label: "ID", value: String(doc.id), icon: "id" },
        ...(scope === SCOPES.general
          ? [
              {
                label: "Product Name",
                value: doc.product_type,
                icon: "title",
              },
            ]
          : [
              {
                label: "Policy No",
                value: doc.policy_no,
                icon: "title",
              },
            ]),
        {
          label: "Document Type",
          value: doc.document_type,
          icon: "category",
        },
        {
          label: "Language",
          value: formatLanguageLabel(doc.language),
          icon: "info",
        },
        {
          label: "File Name",
          value: doc.file_name,
          icon: "link",
        },
        {
          label: "Storage",
          value: doc.storage_type,
          icon: "info",
        },
        {
          label: "Uploaded By",
          value: doc.uploaded_by ? String(doc.uploaded_by) : "—",
          icon: "person",
        },
        ...dateInfoItems(doc),
      ]
    : [];

  return (
    <DetailPageLayout
      title={`${scopeTitle} Policy Document`}
      subtitle="View and manage policy PDF document"
      breadcrumbs={[
        { label: "Policy Documents", path: `/policy-documents?scope=${scope}` },
        { label: `Document (ID - ${id})` },
      ]}
      listPath={`/policy-documents?scope=${scope}`}
      loading={!doc}
      editPath={doc ? `/policy-documents/edit/${scope}/${doc.id}` : undefined}
      onRemove={handleRemove}
      infoItems={infoItems}
      infoTitle="Document Information"
      quickActions={
        doc
          ? [
              {
                label: "Preview PDF",
                onClick: handleDownload,
              },
            ]
          : []
      }
    />
  );
}
